import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
  componentStack: string | null;
}

/** Top-level error boundary — keeps a render crash from blanking the app. */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error?.message ?? String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-flock-navy-50 p-6">
          <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow">
            <img src="/favicon.png" alt="" className="mx-auto mb-3 h-12 w-12" />
            <h1 className="text-lg font-semibold text-flock-navy-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-flock-navy-500">
              Try reloading the page. If it keeps happening, sign out and back in.
            </p>
            {/* Dev-only diagnostics so a crash screenshot identifies the bug */}
            {import.meta.env.DEV && this.state.message && (
              <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-red-50 p-3 text-left text-xs text-red-800">
                {this.state.message}
                {this.state.componentStack
                  ? '\n' + this.state.componentStack.split('\n').slice(0, 6).join('\n')
                  : ''}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="min-h-touch mt-4 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
